import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AccountingDocumentRecord, FinanceApiService, FinancialAccountRecord } from './finance-api.service';

@Component({
  standalone: true,
  selector: 'ain-finance-receipts',
  imports: [AinPageComponent, TableModule, FormsModule, ButtonModule, InputTextModule],
  template: `
    <ain-page [title]="t('finance.receipts.title')" [description]="t('finance.receipts.description')">
      <section class="card list-card">
        <div class="toolbar">
          <input pInputText type="text" [(ngModel)]="searchNumber" [placeholder]="t('finance.receipts.searchNumber')" />
          <button pButton type="button" [label]="t('finance.receipts.search')" (click)="applySearch()"></button>
          <button pButton type="button" [label]="t('finance.receipts.refresh')" severity="secondary" (click)="load()"></button>
        </div>
        <div class="toolbar">
          <button pButton type="button" [label]="t('finance.receipts.addNew')" (click)="openCreate()"></button>
          <button pButton type="button" [label]="t('finance.receipts.editSelected')" severity="secondary" [disabled]="!selected()" (click)="openEditSelected()"></button>
        </div>
        <p-table [value]="filteredRows()" selectionMode="single" [selection]="selected()" (selectionChange)="onSelect($event)" dataKey="id">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('finance.receipts.fields.number') }}</th>
              <th>{{ t('finance.receipts.fields.totalAmount') }}</th>
              <th>{{ t('columns.currency') }}</th>
              <th>{{ t('finance.receipts.fields.accountingAccountId') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr [pSelectableRow]="row">
              <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.number }}</button></td>
              <td>{{ row.amount }}</td>
              <td>{{ row.currencyCode }}</td>
              <td>{{ accountLabel(row.accountingAccountId) }}</td>
            </tr>
          </ng-template>
        </p-table>
        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      </section>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; background: #fff; }
    .list-card { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .link-btn:hover { text-decoration: underline; }
    .message { margin: 0; font-size: 13px; }
    .message.error { color: #b91c1c; }
  `]
})
export class FinanceReceiptsComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);

  readonly rows = signal<(AccountingDocumentRecord & { accountingAccountId?: string })[]>([]);
  readonly filteredRows = signal<(AccountingDocumentRecord & { accountingAccountId?: string })[]>([]);
  readonly accounts = signal<FinancialAccountRecord[]>([]);
  readonly selected = signal<AccountingDocumentRecord | null>(null);
  readonly errorMessage = signal('');
  searchNumber = '';

  ngOnInit(): void {
    this.loadReferenceData();
    this.load();
  }

  load(): void {
    this.errorMessage.set('');
    this.api.listAccountingReceipts().subscribe({
      next: data => {
        this.rows.set(data);
        this.applySearch();
      },
      error: () => {
        this.rows.set([]);
        this.filteredRows.set([]);
        this.selected.set(null);
        this.errorMessage.set(this.t('finance.receipts.messages.loadFailed'));
      }
    });
  }

  applySearch(): void {
    const term = this.searchNumber.trim().toLowerCase();
    const filtered = !term
      ? this.rows()
      : this.rows().filter(item => item.number.toLowerCase().includes(term));
    this.filteredRows.set(filtered);
    this.selected.set(filtered[0] ?? null);
  }

  onSelect(row: AccountingDocumentRecord | null): void {
    this.selected.set(row);
  }

  openCreate(): void {
    void this.router.navigate(['/financial/payments/receipts/new']);
  }

  openEditSelected(): void {
    const row = this.selected();
    if (!row) {
      return;
    }
    this.openDetail(row.id);
  }

  openDetail(id: string): void {
    void this.router.navigate(['/financial/payments/receipts', id]);
  }

  accountLabel(accountId?: string): string {
    if (!accountId?.trim()) {
      return '-';
    }

    const account = this.accounts().find(item => item.id.toLowerCase() === accountId.toLowerCase());
    if (!account) {
      return '-';
    }

    return account.breadcrumb?.trim() || `${account.code} - ${account.name}`;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private loadReferenceData(): void {
    forkJoin({
      accounts: this.api.listFinancialAccounts().pipe(catchError(() => of([])))
    }).subscribe({
      next: ({ accounts }) => this.accounts.set(accounts),
      error: () => this.accounts.set([])
    });
  }
}
